import { useHydrated } from "@tanstack/react-router";
import type {
    CompatibilityProfile,
    ProfileQuestionId,
} from "@wags/matchmaker/pure.ts";
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "compatibility-profile";

function readProfile(): CompatibilityProfile {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object"
            ? (parsed as CompatibilityProfile)
            : {};
    } catch {
        return {};
    }
}

function writeProfile(profile: CompatibilityProfile) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch {}
}

const EMPTY: CompatibilityProfile = {};
let store: CompatibilityProfile | null = null;
const listeners = new Set<() => void>();

function getStore(): CompatibilityProfile {
    if (store === null) store = readProfile();
    return store;
}

function emit() {
    for (const listener of listeners) listener();
}

function subscribe(callback: () => void) {
    listeners.add(callback);
    return () => {
        listeners.delete(callback);
    };
}

function setStoreFact<K extends ProfileQuestionId>(
    key: K,
    value: CompatibilityProfile[K],
) {
    store = { ...getStore(), [key]: value };
    writeProfile(store);
    emit();
}

function resetStore() {
    store = {};
    writeProfile({});
    emit();
}

export type UseCompatibilityProfile = {
    profile: CompatibilityProfile;
    hydrated: boolean;
    setFact: <K extends ProfileQuestionId>(
        key: K,
        value: CompatibilityProfile[K],
    ) => void;
    reset: () => void;
};

export function useCompatibilityProfile(): UseCompatibilityProfile {
    const profile = useSyncExternalStore(subscribe, getStore, () => EMPTY);
    const hydrated = useHydrated();

    return { profile, hydrated, setFact: setStoreFact, reset: resetStore };
}
